import React, {forwardRef, Ref, useId} from "react";
import {components as ReactSelectComponents, GroupBase, OnChangeValue, StylesConfig} from "react-select";
import ReactSelectAsync, {AsyncProps as IReactSelectAsyncProps} from "react-select/async";
import Select from "react-select/base";
import classNames from "classnames";

import {ChevronDownIcon} from "@pg-design/icons-module";

import {useSelectTheme} from "../hooks/use_select_theme";
import {SELECT_TEST_CLASSNAMES} from "../utils/test_ids";
import {NoOptionsMessage} from "./NoOptionsMessage";

import {iconStyle} from "./Select.module.css";

export interface ISelectAsyncProps<Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>
    extends IReactSelectAsyncProps<Option, IsMulti, Group> {
    defaultOptions?: Option[] | boolean;
    noOptionsText?: string;
    isLoading?: boolean;
    loadOptions?: (inputValue: string, callback: (options: Option[]) => void) => Promise<Option[]>;
    loadingMessage?: (obj: {inputValue: string}) => React.ReactNode;
    defaultOptionMessage?: {title?: string; message?: string};
    disableDropdownIndicator?: boolean;
    onChange?: (value: OnChangeValue<Option, IsMulti>) => void;
    styles?: StylesConfig<Option, IsMulti, Group>;
    isBorderless?: boolean;
}

export const SelectAsync = forwardRef(
    <Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
        props: ISelectAsyncProps<Option, IsMulti, Group>,
        forwardedRef: unknown
    ) => {
        const {disableDropdownIndicator, components, placeholder = "Wybierz", noOptionsText, noOptionsMessage, loadingMessage, ...forwardProps} = props;

        const {getTheme, styles} = useSelectTheme<Option, IsMulti, Group>(props.isBorderless);

        const id = useId();

        return (
            <ReactSelectAsync
                components={{
                    DropdownIndicator: (state) => {
                        return disableDropdownIndicator ? null : (
                            <ChevronDownIcon
                                className={classNames(iconStyle, SELECT_TEST_CLASSNAMES.CHEVRON_ICON)}
                                size="2"
                                fill={`var(--colors-gray-${state.isDisabled ? "300" : "700"})`}
                            />
                        );
                    },
                    SingleValue: (state) => {
                        return (
                            <ReactSelectComponents.SingleValue {...state} innerProps={{...state.innerProps, className: SELECT_TEST_CLASSNAMES.SINGLE_VALUE}} />
                        );
                    },
                    MultiValue: (state) => {
                        return (
                            <ReactSelectComponents.MultiValue {...state} innerProps={{...state.innerProps, className: SELECT_TEST_CLASSNAMES.SINGLE_VALUE}} />
                        );
                    },
                    ClearIndicator: (state) => {
                        return (
                            <ReactSelectComponents.ClearIndicator
                                {...state}
                                innerProps={{...state.innerProps, className: SELECT_TEST_CLASSNAMES.CLEAR_FIELD}}
                            />
                        );
                    },
                    MultiValueRemove: (state) => {
                        return (
                            <ReactSelectComponents.MultiValueRemove
                                {...state}
                                innerProps={{...state.innerProps, className: SELECT_TEST_CLASSNAMES.REMOVE_VALUE}}
                            />
                        );
                    },
                    ...components
                }}
                theme={getTheme}
                {...forwardProps}
                instanceId={id || forwardProps.instanceId}
                styles={{...styles, ...props.styles}}
                ref={forwardedRef as Ref<Select<Option, IsMulti, Group>>}
                placeholder={placeholder}
                noOptionsMessage={
                    noOptionsMessage ? noOptionsMessage : ({inputValue}) => <NoOptionsMessage customText={noOptionsText} inputValue={inputValue} />
                }
                loadingMessage={loadingMessage}
            />
        );
    }
);
